import React from 'react';
import SEO from '../components/SEO';
import { Shield, CheckCircle } from 'lucide-react';

export default function ModernSlaveryStatement() {
  return (
    <>
      <SEO
        title="Modern Slavery Statement | UK Compliance"
        description="TacticalSolutions.ai's commitment to preventing modern slavery and human trafficking in our business and supply chains."
        keywords={['Modern Slavery Act', 'Human Trafficking', 'Ethical Supply Chain', 'Corporate Responsibility']}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center space-x-3 mb-8">
            <Shield className="w-10 h-10 text-teal-400" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Modern Slavery Statement
            </h1>
          </div>

          <div className="prose prose-invert prose-teal max-w-none">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-8">
              <p className="text-blue-300 mb-2">
                <strong>Financial Year Ending:</strong> December 2025
              </p>
              <p className="text-gray-300 mb-0">
                This statement is made pursuant to Section 54(1) of the Modern Slavery Act 2015 and constitutes our slavery and human trafficking statement for the financial year ending December 2025.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Introduction</h2>
            <p className="text-gray-300">
              TacticalSolutions.ai is committed to acting ethically and with integrity in all our business relationships and to implementing and enforcing effective systems and controls to ensure slavery and human trafficking is not taking place anywhere in our supply chains.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Organisation Structure</h2>
            <p className="text-gray-300">
              TacticalSolutions.ai Ltd is a UK-based company registered in England and Wales, specializing in autonomous AI automation solutions for UK public sector organizations. Our operations are based in the United Kingdom, and we work primarily with UK government departments, NHS trusts, local councils, and other public bodies.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Our Business</h2>
            <p className="text-gray-300">
              We provide the following services to UK public sector clients:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Robotic Process Automation (RPA)</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Document Processing and AI OCR</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Agentic AI for Citizen Services</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Workflow Automation Solutions</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Our Supply Chains</h2>
            <p className="text-gray-300">
              Our supply chains include:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>Cloud infrastructure providers (Azure UK, AWS UK regions)</li>
              <li>AI and automation platform providers</li>
              <li>Professional services and consultancy</li>
              <li>Office equipment and supplies</li>
              <li>IT hardware and software suppliers</li>
            </ul>
            <p className="text-gray-300 mt-4">
              We prioritize working with UK-based suppliers who share our commitment to ethical business practices.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Our Policies on Slavery and Human Trafficking</h2>
            <p className="text-gray-300">
              We are committed to ensuring that there is no modern slavery or human trafficking in our supply chains or in any part of our business. Our policies reflect our commitment to acting ethically and with integrity in all our business relationships.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Due Diligence Processes</h3>
            <p className="text-gray-300">
              As part of our initiative to identify and mitigate risk, we:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Conduct risk assessments on new suppliers before onboarding</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Review our existing supply chains and contractual relationships</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Require suppliers to demonstrate their compliance with the Modern Slavery Act</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Maintain a preferred supplier list of ethical, compliant partners</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                <span>Protect whistleblowers who report concerns</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Supplier Adherence to Our Values</h3>
            <p className="text-gray-300">
              We have zero tolerance for slavery and human trafficking. We expect all those in our supply chain and contractors to comply with our values. Our standard terms and conditions include provisions requiring suppliers to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>Comply with all applicable laws, statutes, regulations, and codes relating to anti-slavery and human trafficking</li>
              <li>Have and maintain their own policies and procedures to ensure compliance with anti-slavery legislation</li>
              <li>Implement due diligence procedures for their own supply chains</li>
              <li>Notify us immediately if they become aware of any actual or suspected slavery or human trafficking in their supply chain</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Training</h2>
            <p className="text-gray-300">
              To ensure a high level of understanding of the risks of modern slavery and human trafficking in our supply chains and our business, we provide training to relevant members of staff. All new employees receive information on our Modern Slavery Statement as part of their induction.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Effectiveness in Combating Slavery and Human Trafficking</h2>
            <p className="text-gray-300">
              We use the following key performance indicators (KPIs) to measure how effective we have been to ensure that slavery and human trafficking is not taking place in any part of our business or supply chains:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>100% of suppliers required to confirm compliance with Modern Slavery Act</li>
              <li>Regular audits of high-risk suppliers</li>
              <li>Staff training completion rates</li>
              <li>Number of reported concerns and resolution outcomes</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-4">Further Steps</h2>
            <p className="text-gray-300">
              Following a review of the effectiveness of the steps we have taken this year to ensure that there is no slavery or human trafficking in our supply chains, we intend to take the following further steps:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-1">
              <li>Conduct annual supplier audits for compliance verification</li>
              <li>Enhance due diligence procedures for international suppliers</li>
              <li>Increase transparency in our supply chain mapping</li>
              <li>Expand training programs for procurement team</li>
            </ul>

            <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-6 mt-8">
              <h3 className="text-lg font-semibold text-white mb-3">Approval</h3>
              <p className="text-gray-300 mb-4">
                This statement is made pursuant to section 54(1) of the Modern Slavery Act 2015 and constitutes our group's slavery and human trafficking statement for the financial year ending December 2025.
              </p>
              <p className="text-gray-300 mb-2">
                <strong>Approved by:</strong> Anthony Keen
              </p>
              <p className="text-gray-300 mb-2">
                <strong>Title:</strong> Director
              </p>
              <p className="text-gray-300">
                <strong>Date:</strong> January 2025
              </p>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mt-8">
              <h3 className="text-lg font-semibold text-white mb-3">Reporting Concerns</h3>
              <p className="text-gray-300">
                If you have concerns about modern slavery or human trafficking in our business or supply chains, please contact:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 mt-2">
                <li>Email: compliance@tacticalsolutions.ai</li>
                <li>Address: London, United Kingdom</li>
              </ul>
              <p className="text-gray-300 mt-3">
                All reports will be treated confidentially and investigated thoroughly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
